import { Injectable, inject, computed, Signal } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { toSignal } from "@angular/core/rxjs-interop";
import { filter, map, startWith } from "rxjs";
import { PermissionsService } from "./permissions/permissions.service";

/**
 * Interface for sidebar items.
 */
export interface SidebarItem {
  /** Lucide icon name for the sidebar item. */
  lucideIconName: string;

  /** Label for the sidebar item. */
  label: string;

  /** Link for the sidebar item. */
  link: string;

  /** Whether the sidebar item is active. */
  isActive: boolean;
}

@Injectable({
  providedIn: "root",
})
export class SidebarItemsService {
  private readonly _router = inject(Router);
  private readonly _permissionsService = inject(PermissionsService);

  /** Current URL as signal for reactive updates */
  private readonly _currentUrl = toSignal(
    this._router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event: NavigationEnd) => event.urlAfterRedirects),
      startWith(this._router.url),
    ),
    { initialValue: this._router.url },
  );

  /** Allowed navigation items from permissions service */
  private readonly _allowedNavigation =
    this._permissionsService.allowedNavigation;

  /** First section items (main navigation) */
  public readonly firstItems: Signal<SidebarItem[]> = computed(() => {
    const allowedItems = this._allowedNavigation();
    const currentUrl = this._currentUrl();

    // Filter for main navigation items (not services)
    const mainItems = allowedItems.filter((item) =>
      ["/home", "/agenda"].includes(item.path),
    );

    return mainItems.map((item) => ({
      lucideIconName: item.icon,
      label: item.label,
      link: item.path,
      isActive: currentUrl.startsWith(item.path),
    }));
  });

  /** Services section items */
  public readonly servicesItems: Signal<SidebarItem[]> = computed(() => {
    const allowedItems = this._allowedNavigation();
    const currentUrl = this._currentUrl();

    // Filter for service items
    const serviceItems = allowedItems.filter(
      (item) =>
        !["/home", "/agenda", "/account", "/help"].includes(item.path) &&
        item.path !== "",
    );

    return serviceItems.map((item) => ({
      lucideIconName: item.icon,
      label: item.label,
      link: item.path,
      isActive: currentUrl.startsWith(item.path),
    }));
  });

  /** Bottom section items (always visible) */
  public readonly bottomItems: Signal<SidebarItem[]> = computed(() => {
    const currentUrl = this._currentUrl();

    return [
      {
        lucideIconName: "lucideUserRound",
        label: "Account",
        isActive: currentUrl.startsWith("/account"),
        link: "/account",
      },
      {
        lucideIconName: "lucideLogOut",
        label: "Logout",
        isActive: false,
        link: "",
      },
      {
        lucideIconName: "lucideMessageCircleQuestion",
        label: "Help",
        isActive: currentUrl.startsWith("/help"),
        link: "/help",
      },
    ];
  });
}
